// Stateless tier-completion / wrap-up screen.
//
// Extracted from TierWrapUp.tsx so non-lesson surfaces (the admin Play
// Sessions deep-dive) can render the same screen without pulling in
// the lesson Redux slices, router, or media-query hook. The original
// `TierWrapUp` is now a thin wrapper that feeds this component from
// Redux + handles `navigate('/roadmap')` via `onBackClick`.

import { ReactNode } from 'react';
import { Box, SxProps } from '@mui/system';
import { Typography } from '@mui/material';
import LeftHandIcon from 'assets/images/lh-icon.png';
import RightHandIcon from 'assets/images/rh-icon.png';
import Completion from './Completion';
import { CustomCircularProgress, CustomButton } from 'Components/StyledComponents';
import { totalPlayTimetoString } from 'Utils';
import { PlaySessionData, PreviousTierPlaySessions } from 'Types/LessonTypes';
import BarChart from 'Components/D3/TierWrapUp/BarChat';
import OvularNoteCountSymbol from 'Components/TierWrapUp/OvularNoteCountSymbol';
import { range } from 'lodash';
import { MediaQueries } from 'Types/MediaQueries';
import { mediaQueryValues } from 'Utils/MediaQueries';
import { useMediaQueries } from 'Hooks/useMediaQueries';
import { getColorByCbType } from 'Components/Colors';
import { ERROR_REC_CLASS } from 'Utils/Constants';
import { isLastTierOfLevel } from 'Utils/LevelTierCount';

// Inferred from the existing color helper signature so we don't drift if
// the colorblind type definition moves.
type ColorblindType = Parameters<typeof getColorByCbType>[1];

// MUS-295: landscape phones report a wide viewport (e.g. 932px on a Pro Max),
// so a width-only breakpoint reads them as desktop and keeps the big text.
// The real constraint there is the short height, so treat a narrow OR short
// viewport as "compact" and shrink the text/spacing to fit.
const useIsCompact = (): boolean => {
  const { minWidth800, minHeight750 } = useMediaQueries();
  return !minWidth800 || !minHeight750;
};

const Grid = ({
  children,
  mediaQueries: _mediaQueries = {
    maxHeight: mediaQueryValues.minHeight1100,
    maxWidth: mediaQueryValues.minWidth800,
  },
}: {
  children?: ReactNode;
  mediaQueries?: MediaQueries;
}) => {
  const isCompact = useIsCompact();
  return (
    <Box
      sx={{
        justifyContent: 'center',
        gridGap: isCompact ? '8px' : { xs: '6px', md: '10px' },
        display: 'grid',
        gridTemplateColumns: '[col1-start] 1fr  [col2-start] 1fr  [col3-start] 1fr [col3-end]',
        background: 'linear-gradient(240.8deg, #1D2F44 -56.75%, #071423 210.02%)',
        padding: isCompact ? '12px 10px' : { xs: '16px 10px', md: '24px 16px', lg: '30px 20px' },
        minHeight: isCompact ? '160px' : { xs: '120px', md: '220px', lg: '260px' },
        height: { xs: 'auto', lg: '40%' },
        width: { xs: '94%', md: '90%', lg: '80%' },
      }}
    >
      {children}
    </Box>
  );
};

const DataCard = ({
  children,
  gridColumn,
  gridRow = '1',
  sx,
}: {
  children?: ReactNode;
  gridColumn: string;
  gridRow?: string;
  sx?: SxProps;
}) => {
  return (
    <Box
      sx={{
        background: '#2E263E',
        color: '#fff',
        borderRadius: '5px',
        padding: { xs: '8px', md: '10px' },
        textAlign: 'left',
        minWidth: 0,
        gridColumn,
        gridRow,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

type DivisorAlignment = 'vertical' | 'horizontal';

const DivisorLine = ({
  alignment = 'horizontal',
  height = '100%',
}: {
  alignment?: DivisorAlignment;
  height?: string | number;
}) => (
  <Box
    sx={{
      boxShadow: '0 0 0 0.5px rgba(206, 208, 212, 0.5)',
      width: alignment === 'horizontal' ? height : 0,
      height: alignment === 'vertical' ? height : 0,
    }}
  />
);

const InfoNumber = ({ children }: { children?: ReactNode }) => {
  const isCompact = useIsCompact();
  return (
    <Typography
      variant="h5"
      sx={{
        color: '#6F87F1',
        paddingTop: isCompact ? '2px' : { xs: '4px', md: '8px', lg: '10px' },
        fontSize: isCompact ? '15pt' : { xs: '14pt', sm: '16pt', md: '18pt', lg: '20pt' },
      }}
    >
      {children}
    </Typography>
  );
};

const SubDataCard = ({
  children,
  withBackground = false,
  sx,
  mediaQueries: _mediaQueries = {
    maxHeight: mediaQueryValues.minHeight1100,
    maxWidth: mediaQueryValues.minWidth800,
  },
}: {
  children?: ReactNode;
  withBackground?: boolean;
  sx?: SxProps;
  mediaQueries: MediaQueries;
}) => {
  const isCompact = useIsCompact();
  return (
    <Box
      sx={{
        background: withBackground ? '#7F81A01A' : 'none',
        marginTop: { xs: '6px', md: '10px' },
        borderRadius: '5px',
        color: '#CED0D4',
        fontSize: isCompact ? '11pt' : { xs: '10pt', sm: '11pt', md: '14pt', lg: '18pt' },
        height: '45%',
        width: '100%',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
};

export const TierWrapUpEmptyState = () => {
  return (
    <Grid>
      <CustomCircularProgress
        sx={{
          position: 'absolute',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: 'auto',
          left: 0,
          right: 0,
        }}
      />
      <DataCard gridColumn="col1-start / col2-start" />
      <DataCard gridColumn="col2-start / col3-start" />
      <DataCard gridColumn="col3-start" />
    </Grid>
  );
};

const OvularNoteCount = ({ numNotes, color }: { numNotes: number; color: string }) => {
  const isCompact = useIsCompact();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        '& h3': {
          marginTop: '3px',
          marginBottom: 0,
          color: color,
          fontSize: isCompact ? '17px' : { xs: '18px', md: '22px', lg: '26px' },
        },
      }}
    >
      <OvularNoteCountSymbol height={30} fill={color} />
      <h3>{numNotes}</h3>
    </Box>
  );
};

// MUS-295: per-hand accuracy block. The icon and LEFT/RIGHT label are always
// stacked vertically; on compact (mobile / landscape phone) the percentage
// sits beside that stack (row) to save vertical space, otherwise it sits
// below (column) as before.
const HandAccuracy = ({
  icon,
  label,
  value,
}: {
  icon: string;
  label: string;
  value: ReactNode;
}) => {
  const isCompact = useIsCompact();
  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: isCompact ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: isCompact ? '10px' : 0,
        fontSize: { xs: '6pt', sm: '8pt', md: '10pt' },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          component="img"
          src={icon}
          sx={{
            width: { xs: '18px', sm: '28px', md: '36px', lg: '45px' },
            height: { xs: '18px', sm: '28px', md: '36px', lg: '45px' },
          }}
        />
        {label}
      </Box>
      <InfoNumber>{value}</InfoNumber>
    </Box>
  );
};

export type TierWrapUpDisplayProps = {
  // The tier session data to render. When null + `loading` is true we
  // render the empty-state skeleton instead of the full screen.
  tierSummary: PlaySessionData | null;
  currentUserLevelData?: { current_tier?: number } | null;
  // MUS-651: null while the level's tier count is not known.
  currentLevelTiers: number | null;
  phrasesTilNextTier?: number;
  colorblindType: ColorblindType;
  mediaQueries: MediaQueries;
  // From useMediaQueries().maxHeight — drives the top-margin breakpoint.
  maxHeight: number;
  loading?: boolean;
  // Show the "BACK TO ROADMAP"-style button when set. Hidden when no
  // callback is provided OR when the user is already done with this tier.
  onBackClick?: () => void;
  backLabel?: string;
  // Optional debug controls slot (Load/Clear inputs) rendered above the
  // header. The wrapper passes its own Redux-aware controls in dev mode;
  // admin and prod pass nothing.
  debugSlot?: ReactNode;
  // Show the dev-only "play session summary id: …" footer.
  showSessionIdFooter?: boolean;
};

const TierWrapUpDisplay = ({
  tierSummary,
  currentUserLevelData,
  currentLevelTiers,
  phrasesTilNextTier,
  colorblindType,
  mediaQueries,
  maxHeight,
  loading = false,
  onBackClick,
  backLabel = 'BACK TO ROADMAP',
  debugSlot,
  showSessionIdFooter = false,
}: TierWrapUpDisplayProps) => {
  const isCompact = useIsCompact();
  const levelComplete = isLastTierOfLevel(currentUserLevelData?.current_tier, currentLevelTiers);
  // The tiers the chart can show: past the summarised tier only when the count is known.
  const chartTierCount = currentLevelTiers ?? tierSummary?.tier_number ?? 1;

  if (!tierSummary && !loading) return null;

  const barChartData =
    tierSummary?.previousTiersPlaySessions
      .map((tierSession: PreviousTierPlaySessions) => ({
        tier: tierSession.tier_number,
        time: tierSession.total_play_time,
      }))
      .concat([
        {
          tier: tierSummary.tier_number,
          time: tierSummary.total_play_time,
        },
      ])
      .concat(
        range(tierSummary.tier_number + 1, chartTierCount + 1).map((curr) => ({
          tier: curr,
          time: 0,
        })),
      )
      .sort((a, b) => a.tier - b.tier) || [];

  return (
    <>
      <Box
        sx={{
          marginTop: maxHeight > mediaQueryValues.minHeight900 ? '80px' : '10px',
          marginBottom: { xs: '4px', md: '10px' },
          fontWeight: '400',
          fontStyle: 'normal',
          fontSize: isCompact ? '24px' : { xs: '22px', sm: '26px', md: '30px', lg: '34px' },
          lineHeight: isCompact ? '30px' : { xs: '28px', md: '36px', lg: '41px' },
          color: '#469F6A',
          letterSpacing: '0.01em',
          fontFamily: 'Lato',
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {debugSlot}
        {phrasesTilNextTier == 0 && (
          <>
            {levelComplete
              ? 'Level'
              : currentLevelTiers === null
                ? `Tier ${currentUserLevelData?.current_tier}`
                : `Tier ${currentUserLevelData?.current_tier} of ${currentLevelTiers}`}{' '}
            Complete!
          </>
        )}

        {phrasesTilNextTier != 0 && <>Session Wrap-Up</>}
      </Box>
      {showSessionIdFooter && (
        <Box sx={{ color: 'white', padding: '10px' }}>
          play session summary id: {tierSummary?.id}
        </Box>
      )}
      {phrasesTilNextTier == 0 && <Completion />}
      {tierSummary !== null && (
        <>
          <Grid mediaQueries={mediaQueries}>
            <DataCard gridColumn="col1-start / col2-start">
              <SubDataCard
                sx={{
                  paddingLeft: { xs: '10px', md: '16px', lg: '20px' },
                }}
                mediaQueries={mediaQueries}
              >
                TOTAL PLAY TIME
                <InfoNumber>{totalPlayTimetoString(tierSummary.total_play_time)}</InfoNumber>
              </SubDataCard>
              <DivisorLine />
              <SubDataCard
                withBackground={true}
                mediaQueries={mediaQueries}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <BarChart
                  data={barChartData}
                  currentLevelTiers={chartTierCount}
                  currentTier={currentUserLevelData?.current_tier}
                />
              </SubDataCard>
            </DataCard>
            <DataCard gridColumn="col2-start / col3-start" gridRow="1">
              <SubDataCard
                sx={{
                  paddingLeft: { xs: '10px', md: '16px', lg: '20px' },
                }}
                mediaQueries={mediaQueries}
              >
                AVERAGE ACCURACY
                <InfoNumber>{Math.round(tierSummary.avg_accuracy)}%</InfoNumber>
              </SubDataCard>
              <DivisorLine />
              <SubDataCard
                withBackground={true}
                sx={{
                  height: '45%',
                }}
                mediaQueries={mediaQueries}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <HandAccuracy
                    icon={LeftHandIcon}
                    label="LEFT"
                    value={
                      tierSummary?.avg_lh_accuracy
                        ? Math.round(tierSummary?.avg_lh_accuracy) + '%'
                        : '--'
                    }
                  />
                  <DivisorLine alignment="vertical" height="80%" />
                  <HandAccuracy
                    icon={RightHandIcon}
                    label="RIGHT"
                    value={
                      tierSummary?.avg_rh_accuracy
                        ? Math.round(tierSummary?.avg_rh_accuracy) + '%'
                        : '--'
                    }
                  />
                </Box>
              </SubDataCard>
            </DataCard>
            <DataCard gridColumn="col3-start">
              <SubDataCard
                sx={{
                  paddingLeft: { xs: '10px', md: '16px', lg: '20px' },
                }}
                mediaQueries={mediaQueries}
              >
                NOTES PLAYED
                <InfoNumber>{tierSummary.played_notes}</InfoNumber>
              </SubDataCard>
              <DivisorLine />
              <SubDataCard
                withBackground={true}
                sx={{
                  height: '45%',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 10%',
                  width: '80%',
                }}
                mediaQueries={mediaQueries}
              >
                <OvularNoteCount
                  numNotes={tierSummary.non_errors}
                  color={getColorByCbType(ERROR_REC_CLASS.CORRECT, colorblindType)}
                />
                <DivisorLine alignment="vertical" height="80%" />
                <OvularNoteCount
                  numNotes={tierSummary.semi_errors}
                  color={getColorByCbType(ERROR_REC_CLASS.SEMI_ERROR, colorblindType)}
                />
                <DivisorLine alignment="vertical" height="80%" />
                <OvularNoteCount
                  numNotes={tierSummary.errors}
                  color={getColorByCbType(ERROR_REC_CLASS.ERROR, colorblindType)}
                />
              </SubDataCard>
            </DataCard>
          </Grid>
          {onBackClick && phrasesTilNextTier !== 0 && (
            <CustomButton
              sx={{
                marginTop: { xs: '12px', md: '20px' },
                width: { xs: '320px', md: '260px', lg: '320px' },
                height: { xs: '28px', md: '58px', lg: '70px' },
                fontSize: { xs: '12px', md: '14px' },
              }}
              onClick={onBackClick}
            >
              {backLabel}
            </CustomButton>
          )}
        </>
      )}
      {tierSummary === null && loading && <TierWrapUpEmptyState />}
    </>
  );
};

export default TierWrapUpDisplay;
